import { useState, useEffect, useRef } from "react";
import { navItems } from "../constants/data";
import { notificationAPI, announcementAPI } from "../services/api";
import { Modal, Btn } from "./shared/index";
import { useLanguage } from "../context/LanguageContext";
import {
  MdNotifications, MdPersonOutline, MdLogout, MdGridView, MdLockOutline,
  MdEventNote, MdDescription, MdCampaign, MdInfo, MdDoneAll, MdNotificationsNone,
  MdLanguage,
} from "react-icons/md";

const TYPE_LABEL = {
  MEETING:"កិច្ចប្រជុំ", DOCUMENT:"ឯកសារ", ANNOUNCEMENT:"ការប្រកាស", SYSTEM:"ប្រព័ន្ធ",
};

const BORDER = "var(--border)";
const BROWN  = "var(--brown)";
const MUTED  = "var(--muted)";
const BG     = "var(--cream)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const TYPE_ICON = {
  MEETING:      <MdEventNote size={16} />,
  DOCUMENT:     <MdDescription size={16} />,
  ANNOUNCEMENT: <MdCampaign size={16} />,
  SYSTEM:       <MdInfo size={16} />,
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)     return "មុននេះបន្តិច";
  if (diff < 3600)   return `${Math.floor(diff/60)} នាទីមុន`;
  if (diff < 86400)  return `${Math.floor(diff/3600)} ម៉ោងមុន`;
  if (diff < 604800) return `${Math.floor(diff/86400)} ថ្ងៃមុន`;
  return new Date(iso).toLocaleDateString("en-GB");
}

function LanguageSwitch() {
  const { lang, setLang } = useLanguage();
  return (
    <button
      onClick={() => setLang(lang === "km" ? "en" : "km")}
      title={lang === "km" ? "Switch to English" : "ប្តូរទៅភាសាខ្មែរ"}
      style={{
        display:"flex", alignItems:"center", gap:5,
        height:36, padding:"0 12px", borderRadius:18,
        border:`1px solid ${BORDER}`, background:BG, color:BROWN,
        cursor:"pointer", fontSize:12.5, fontWeight:700, fontFamily:KH,
      }}>
      <MdLanguage size={16} />
      {lang === "km" ? "ខ្មែរ" : "EN"}
    </button>
  );
}

const TYPE_NAV = {
  MEETING:      "meeting-list",
  DOCUMENT:     "doc-officers",
  ANNOUNCEMENT: "ann-list",
};

const REF_FIELD_LABEL = {
  meetingId:       "កិច្ចប្រជុំ",
  roomName:        "បន្ទប់",
  organizerName:   "អ្នករៀបចំ",
  senderName:      "អ្នកផ្ញើ",
  createdByName:   "បង្កើតដោយ",
  departmentName:  "នាយកដ្ឋាន",
  documentName:    "ឯកសារ",
  officerName:     "មន្ត្រី",
};

function NotificationBell({ user, onNavigate }) {
  const { t } = useLanguage();
  const userId = user?.id;
  const [open,    setOpen]    = useState(false);
  const [list,    setList]    = useState([]);
  const [count,   setCount]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail,  setDetail]  = useState(null);
  const wrapRef = useRef(null);

  const loadCount = async () => {
    if (!userId) return;
    try {
      const res = await notificationAPI.getUnreadCount(userId);
      const n = res?.data?.data ?? res?.data ?? 0;
      setCount(typeof n === "number" ? n : Number(n) || 0);
    } catch {}
  };

  const loadList = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res  = await notificationAPI.getMy(userId, 0, 20);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadCount();
    const intervalId = setInterval(loadCount, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const getNid = (n) => n.notificationId ?? n.id;

  const openDetail = async (n) => {
    setDetail(n);
    setOpen(false);
    // An announcement's read state lives on the announcement, not on the
    // notification — mark both, or /read-status keeps reporting it unread.
    if (n.type === "ANNOUNCEMENT" && n.referenceId) {
      announcementAPI.markAsRead(n.referenceId).catch(() => {});
    }
    if (n.isRead) return;
    try {
      await notificationAPI.markAsRead(getNid(n));
      setList(prev => prev.map(x =>
        getNid(x) === getNid(n) ? { ...x, isRead: true } : x));
      setCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const handleReadAll = async () => {
    try {
      await notificationAPI.markAllRead(userId);
      setList(prev => prev.map(x => ({ ...x, isRead: true })));
      setCount(0);
    } catch {}
  };

  return (
    <div ref={wrapRef} data-guide="bell" style={{ position:"relative" }}>
      <button onClick={toggle} title={t("ការជូនដំណឹង")}
        style={{
          width:36, height:36, borderRadius:"50%",
          border:`1px solid ${BORDER}`,
          background: open ? BROWN : BG,
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", position:"relative", padding:0,
        }}>
        <MdNotifications size={18} style={{ color: open ? "#fff" : MUTED }} />
        {count > 0 && (
          <span style={{
            position:"absolute", top:-2, right:-2,
            minWidth:17, height:17, padding:"0 4px",
            borderRadius:9, background:"#EF4444", color:"#fff",
            fontSize:10, fontWeight:700, lineHeight:"17px",
            textAlign:"center", border:"2px solid #fff",
            fontFamily:KH,
          }}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position:"absolute", top:46, right:0, width:340,
          maxHeight:440, background:"var(--surface)", borderRadius:12,
          border:`1px solid ${BORDER}`, overflow:"hidden",
          boxShadow:"0 12px 40px rgba(92,45,14,0.18)", zIndex:1000,
          display:"flex", flexDirection:"column",
        }}>
          <div style={{ display:"flex", alignItems:"center",
                        justifyContent:"space-between",
                        padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
            <p style={{ fontSize:14, fontWeight:700, color:BROWN,
                        fontFamily:MO, margin:0 }}>
              {t("ការជូនដំណឹង")}
            </p>
            {count > 0 && (
              <button onClick={handleReadAll}
                style={{ display:"flex", alignItems:"center", gap:4,
                         background:"none", border:"none", cursor:"pointer",
                         fontSize:11, color:BROWN, fontFamily:KH }}>
                <MdDoneAll size={14} /> {t("អានទាំងអស់")}
              </button>
            )}
          </div>

          <div style={{ overflowY:"auto", flex:1 }}>
            {loading ? (
              <p style={{ padding:"28px", textAlign:"center", color:MUTED,
                          fontSize:13, fontFamily:KH }}>{t("កំពុងផ្ទុក...")}</p>
            ) : list.length === 0 ? (
              <div style={{ padding:"36px 20px", textAlign:"center" }}>
                <MdNotificationsNone size={36} style={{ color:BORDER }} />
                <p style={{ marginTop:8, color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានការជូនដំណឹង")}
                </p>
              </div>
            ) : list.map(n => (
              <div key={getNid(n)} onClick={() => openDetail(n)}
                style={{
                  display:"flex", gap:10, padding:"12px 16px",
                  borderBottom:`1px solid ${BORDER}`, cursor:"pointer",
                  background: n.isRead ? "var(--surface)" : "var(--cream)",
                }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = n.isRead ? "var(--surface)" : "var(--cream)"}>
                <span style={{
                  flexShrink:0, width:32, height:32, borderRadius:"50%",
                  background:BG, color:BROWN,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  {TYPE_ICON[n.type] || <MdInfo size={16} />}
                </span>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:13, fontWeight: n.isRead ? 500 : 700,
                              color:"var(--text)", fontFamily:KH, margin:0,
                              lineHeight:1.4 }}>
                    {n.title || "—"}
                  </p>
                  {n.message && (
                    <p style={{ fontSize:12, color:MUTED, fontFamily:KH,
                                margin:"2px 0 0", lineHeight:1.5 }}>
                      {n.message}
                    </p>
                  )}
                  <p style={{ fontSize:10, color:MUTED, fontFamily:KH,
                              margin:"4px 0 0" }}>
                    {timeAgo(n.createdAt)}
                  </p>
                </div>
                {!n.isRead && (
                  <span style={{ flexShrink:0, width:8, height:8, marginTop:6,
                                 borderRadius:"50%", background:"#EF4444" }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {detail && (() => {
        const refEntries = Object.entries(detail.referenceData || {})
          .filter(([, v]) => v !== null && v !== undefined && v !== "");
        const navKey = TYPE_NAV[detail.type];
        return (
          <Modal
            title={detail.title || t("ការជូនដំណឹង")}
            subtitle={detail.typeLabel || t(TYPE_LABEL[detail.type] || detail.type)}
            onClose={() => setDetail(null)}
          >
            <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                        lineHeight:1.7, whiteSpace:"pre-wrap", marginBottom:14 }}>
              {detail.message || "—"}
            </p>
            {[
              { label:"ប្រភេទ",   value: detail.typeLabel || t(TYPE_LABEL[detail.type] || detail.type) },
              { label:"លេខយោង",  value: detail.referenceId ?? "—" },
              { label:"ថ្ងៃផ្ញើ",  value: detail.createdAt ? new Date(detail.createdAt).toLocaleString("en-GB") : "—" },
              ...(detail.isRead && detail.readAt
                ? [{ label:"ថ្ងៃអាន", value: new Date(detail.readAt).toLocaleString("en-GB") }]
                : []),
              ...refEntries.map(([k, v]) => ({ label: REF_FIELD_LABEL[k] || k, value: String(v) })),
            ].map((r,i) => (
              <div key={i} style={{ display:"flex", padding:"8px 0",
                                    borderBottom:`1px solid ${BORDER}` }}>
                <span style={{ fontSize:12, color:MUTED, minWidth:120, fontFamily:KH }}>{t(r.label)}</span>
                <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH }}>{r.value}</span>
              </div>
            ))}
            <div style={{ marginTop:20, display:"flex", justifyContent:"space-between" }}>
              {navKey && onNavigate ? (
                <Btn onClick={() => { onNavigate(navKey); setDetail(null); }}>
                  {t("មើលល្អិត")}
                </Btn>
              ) : <span />}
              <Btn variant="outline" onClick={() => setDetail(null)}>{t("បិទ")}</Btn>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

const menuItem = {
  width:"100%", display:"flex", alignItems:"center", gap:12,
  padding:"10px 14px", borderRadius:8, background:"transparent",
  border:"none", cursor:"pointer", fontSize:13.5, color:"var(--text)",
  fontFamily:KH, textAlign:"left", transition:"background 0.13s",
};

function UserMenu({ user, onLogout, onProfileClick }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const displayName = user?.userNameKh || user?.userNameEn || t("អ្នកប្រើប្រាស់");

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={wrapRef} data-guide="user-menu" style={{ position:"relative" }}>
      <button onClick={() => setOpen(o => !o)} title={displayName}
        style={{
          display:"flex", alignItems:"center", gap:9,
          padding:"3px 10px 3px 12px", borderRadius:22,
          border:`1px solid ${open ? BORDER : "transparent"}`,
          background: open ? BG : "transparent",
          cursor:"pointer", transition:"background 0.15s, border-color 0.15s",
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = BG; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}>

        <div style={{ textAlign:"right", lineHeight:1.25, maxWidth:160 }}>
          <p style={{ fontSize:12.5, fontWeight:600, color:BROWN,
                      fontFamily:KH, margin:0, whiteSpace:"nowrap",
                      overflow:"hidden", textOverflow:"ellipsis" }}>
            {displayName}
          </p>
          {user?.roleName && (
            <p style={{ fontSize:10, color:MUTED, fontFamily:KH, margin:0,
                        whiteSpace:"nowrap", overflow:"hidden",
                        textOverflow:"ellipsis" }}>
              {user.roleName}
            </p>
          )}
        </div>

        <span style={{
          width:36, height:36, borderRadius:"50%",
          border:`1px solid ${BORDER}`,
          background: open ? BROWN : "var(--surface)",
          display:"flex", alignItems:"center", justifyContent:"center",
          flexShrink:0,
        }}>
          <MdPersonOutline size={20} style={{ color: open ? "#fff" : MUTED }} />
        </span>
      </button>

      {open && (
        <div style={{
          position:"absolute", top:52, right:0, width:262,
          background:"var(--surface)", borderRadius:12, border:`1px solid ${BORDER}`,
          overflow:"hidden", boxShadow:"0 12px 40px rgba(92,45,14,0.18)",
          zIndex:1000, padding:"6px 0",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12,
                        padding:"12px 14px" }}>
            <div style={{
              width:40, height:40, borderRadius:"50%", background:BG,
              border:`1px solid ${BORDER}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
              <MdPersonOutline size={22} style={{ color:MUTED }} />
            </div>
            <div style={{ minWidth:0 }}>
              <p style={{ fontSize:13.5, fontWeight:700, color:"var(--text)", fontFamily:KH,
                          margin:0, whiteSpace:"nowrap", overflow:"hidden",
                          textOverflow:"ellipsis" }}>
                {displayName}
              </p>
              <p style={{ fontSize:11.5, color:MUTED, fontFamily:KH,
                          margin:"2px 0 0", whiteSpace:"nowrap",
                          overflow:"hidden", textOverflow:"ellipsis" }}>
                {user?.email ? `@ ${user.email}` : (user?.roleName || "")}
              </p>
            </div>
          </div>

          <div style={{ height:1, background:BORDER, margin:"2px 0 6px" }} />

          <div style={{ padding:"0 6px" }}>
            <button onClick={() => { setOpen(false); onProfileClick && onProfileClick("view"); }}
              style={menuItem}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <MdGridView size={18} style={{ color:MUTED, flexShrink:0 }} />
              {t("ព័ត៌មានអ្នកប្រើប្រាស់")}
            </button>

            <button onClick={() => { setOpen(false); onProfileClick && onProfileClick("password"); }}
              style={menuItem}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <MdLockOutline size={18} style={{ color:MUTED, flexShrink:0 }} />
              {t("ប្តូរលេខសម្ងាត់")}
            </button>

            <button onClick={() => { setOpen(false); onLogout && onLogout(); }}
              style={menuItem}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <MdLogout size={18} style={{ color:MUTED, flexShrink:0 }} />
              {t("ចាកចេញ")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Topbar({ activeNav, user, onLogout, onProfileClick, onNavigate, onGuide }) {
  const { t } = useLanguage();
  const current = navItems.find(n => n.key === activeNav)
    || navItems.flatMap(n => n.children||[]).find(c => c.key === activeNav);

  return (
    <div style={{
      background:"var(--surface)",
      borderBottom:`1px solid ${BORDER}`,
      padding:"0 24px",
      height:60,
      display:"flex", justifyContent:"space-between", alignItems:"center",
      flexShrink:0,
      boxShadow:"0 1px 4px rgba(92,45,14,0.06)",
    }}>
      <div>
        <p style={{ fontSize:10, color:MUTED, fontFamily:KH, marginBottom:1 }}>
          {t("ប្រព័ន្ធគ្រប់គ្រងអគ្គាធិការដ្ឋាន")}
        </p>
        <h2 style={{ fontSize:16, fontWeight:700, color:BROWN, fontFamily:MO, margin:0 }}>
          {t(current?.label || "ផ្ទាំងគ្រប់គ្រង")}
        </h2>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:10 }}>

        {onGuide && (
          <button data-guide="help" onClick={onGuide} title={t("ណែនាំការប្រើប្រាស់")}
            aria-label={t("ណែនាំការប្រើប្រាស់")}
            style={{ width:36, height:36, borderRadius:"50%", border:`1px solid ${BORDER}`,
                     background:BG, cursor:"pointer", display:"flex", alignItems:"center",
                     justifyContent:"center", color:BROWN, fontSize:16, fontWeight:700 }}>
            ?
          </button>
        )}

        <LanguageSwitch />

        <div style={{ width:1, height:28, background:BORDER }} />

        <NotificationBell user={user} onNavigate={onNavigate} />

        <div style={{ width:1, height:28, background:BORDER }} />

        <UserMenu user={user} onLogout={onLogout} onProfileClick={onProfileClick} />
      </div>
    </div>
  );
}